/**
 * Tenant filter selector for the analytics system view (Sprint 209 —
 * ISS-330 / REQ-ANL-001, fix ISS-372). Only rendered for system admins;
 * "all" keeps the aggregated cross-tenant view.
 *
 * Searchable combobox: queries GET /system/tenants?q=<search> server-side
 * (debounced), paginated within the backend limit (max 100, default page 50).
 * Shows a visible error state when the listing fails.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useI18n } from '@/i18n';
import { useSystemTenants } from '@/features/system/hooks';

interface TenantSelectorProps {
  value: string | null;
  onChange: (tenantId: string | null) => void;
}

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_CHARS = 2;
const PAGE_SIZE = 50;

export function TenantSelector({ value, onChange }: TenantSelectorProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveSearch =
    debouncedSearch.trim().length >= MIN_SEARCH_CHARS ? debouncedSearch.trim() : '';

  const { data: tenantsData, isLoading, error } = useSystemTenants({
    limit: PAGE_SIZE,
    search: effectiveSearch || undefined,
  });

  const tenants = useMemo(() => tenantsData?.data ?? [], [tenantsData]);

  // Debounce the search input so each keystroke does not hit the
  // system/tenants endpoint (rate limited at 10 req/min).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [effectiveSearch, tenantsData]);

  const isSearching = effectiveSearch.length > 0;

  const handleSelect = (tenantId: string | null, tenantName: string | null) => {
    onChange(tenantId);
    setSelectedName(tenantName);
    setIsOpen(false);
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setSearch('');
    setDebouncedSearch('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, tenants.length)); // +1 for "All tenants"
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex === 0) {
        handleSelect(null, null);
      } else {
        const tenant = tenants[highlightedIndex - 1];
        if (tenant) handleSelect(tenant.id, tenant.name);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative flex items-center gap-2">
      <label
        htmlFor="analytics-tenant-filter"
        className="text-sm text-muted-foreground whitespace-nowrap"
      >
        {t('admin.analytics.tenantFilter') || 'Tenant'}
      </label>
      <div
        className={clsx(
          'flex items-center rounded-lg border bg-white dark:bg-gray-800',
          'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20',
          'border-gray-300 dark:border-gray-600'
        )}
      >
        <input
          id="analytics-tenant-filter"
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="analytics-tenant-listbox"
          aria-autocomplete="list"
          value={isOpen && !selectedName ? search : selectedName ?? search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
            setSelectedName(null);
            if (blurTimer.current) clearTimeout(blurTimer.current);
          }}
          onFocus={() => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
            setIsOpen(true);
          }}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setIsOpen(false), 150);
          }}
          onKeyDown={handleKeyDown}
          disabled={isLoading && !tenants.length}
          placeholder={
            selectedName ??
            t('admin.analytics.tenantSearchPlaceholder') ??
            'Search tenant...'
          }
          className={clsx(
            'flex-1 border-0 bg-transparent px-3 py-1.5 text-sm',
            'focus:outline-none focus:ring-0',
            'disabled:cursor-not-allowed',
            'dark:text-gray-100 dark:placeholder-gray-400'
          )}
        />
        {isLoading && (
          <div className="px-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !error && !isLoading && (
        <div
          id="analytics-tenant-listbox"
          role="listbox"
          className={clsx(
            'absolute top-full right-0 z-50 mt-1 max-h-60 w-64 overflow-auto rounded-md',
            'border border-gray-200 bg-white shadow-lg',
            'dark:border-gray-700 dark:bg-gray-800'
          )}
        >
          <button
            type="button"
            role="option"
            aria-selected={value === null}
            onClick={() => handleSelect(null, null)}
            className={clsx(
              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
              'hover:bg-gray-100 dark:hover:bg-gray-700',
              highlightedIndex === 0 && 'bg-gray-100 dark:bg-gray-700'
            )}
          >
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {t('admin.analytics.allTenants') || 'All tenants'}
            </span>
          </button>
          {tenants.map((tenant, index) => (
            <button
              key={tenant.id}
              type="button"
              role="option"
              aria-selected={value === tenant.id}
              onClick={() => handleSelect(tenant.id, tenant.name)}
              onMouseEnter={() => setHighlightedIndex(index + 1)}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                value === tenant.id && 'bg-blue-50 dark:bg-blue-900/20',
                highlightedIndex === index + 1 && 'bg-gray-100 dark:bg-gray-700'
              )}
            >
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {tenant.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Loading dropdown */}
      {isOpen && isLoading && (
        <div
          className={clsx(
            'absolute top-full right-0 z-50 mt-1 w-64 rounded-md p-3 text-center text-sm',
            'border border-gray-200 bg-white text-gray-500',
            'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
          )}
        >
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 mx-auto" />
        </div>
      )}

      {/* Error state — visible, never silent */}
      {isOpen && error && (
        <div
          className={clsx(
            'absolute top-full right-0 z-50 mt-1 w-64 rounded-md p-3 text-center text-sm',
            'border border-red-200 bg-red-50 text-red-700',
            'dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
          )}
        >
          {t('admin.analytics.tenantSearchError') ||
            'Failed to load tenants. Please try again.'}
        </div>
      )}

      {/* No results */}
      {isOpen && !isLoading && !error && isSearching && tenants.length === 0 && (
        <div
          className={clsx(
            'absolute top-full right-0 z-50 mt-1 w-64 rounded-md p-3 text-center text-sm',
            'border border-gray-200 bg-white text-gray-500',
            'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
          )}
        >
          {t('admin.analytics.tenantSearchNoResults') || 'No tenants found'}
        </div>
      )}
    </div>
  );
}